import { css, cx } from "@emotion/css";
import { Badge, DeprecatedIconButton, LegacyDataTable, LegacyDataTableBody, LegacyDataTableHeader, LegacyDataTableRow, LegacyDataTableRowColumn, Tooltip } from "@octopusdeploy/design-system-components";
import { UnavailableIcon } from "@octopusdeploy/design-system-icons";
import { text, themeTokens, space } from "@octopusdeploy/design-system-tokens";
import type { NamedResource, KubernetesLiveStatusResource, KubernetesMachineLiveStatusResource, KubernetesArgoCDInstanceLiveStatusResource, ArgoCDInstanceSummaryResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { keyBy } from "lodash";
import * as React from "react";
import { useMemo } from "react";
import { K8sResourceIcon } from "~/areas/projects/components/Observability/KubernetesResources";
import { LiveStatusResourceHealthStatusIcon, LiveStatusResourceSyncStatusIcon, MachineStatusIcon } from "~/areas/projects/components/Observability/LiveStatusIcons";
import { StatusDisplayLookup } from "~/areas/projects/components/Observability/StatusDisplayLookup";
import InternalLink from "~/components/Navigation/InternalLink";
type OnResourceSelected = (resource: KubernetesLiveStatusResource) => void;
type LiveStatusTableProps = {
    spaceId: string;
    deploymentTargets: NamedResource[];
    machineLiveStatusResources: KubernetesMachineLiveStatusResource[];
    argoCDInstanceLiveStatusResources: KubernetesArgoCDInstanceLiveStatusResource[];
    argoCDInstances: ArgoCDInstanceSummaryResource[];
    resourceSelected: OnResourceSelected;
};
const emptyNamespaceDisplay = "-";
// Helper functions for auto-expansion logic
const FAILING_HEALTH_STATUSES = ["Degraded", "Missing", "Unknown"] as const;
const isFailingHealthStatus = (healthStatus: string): boolean => {
    return FAILING_HEALTH_STATUSES.some((status) => status === healthStatus);
};
const hasFailingResourcesRecursively = (resource: KubernetesLiveStatusResource): boolean => {
    if (!resource)
        return false;
    const isFailing = isFailingHealthStatus(resource.HealthStatus);
    const hasFailingChildren = resource.Children?.some((child) => hasFailingResourcesRecursively(child)) ?? false;
    return isFailing || hasFailingChildren;
};
const useAutoExpansion = (shouldExpand: boolean) => {
    const [open, setOpen] = React.useState(shouldExpand);
    React.useEffect(() => {
        if (shouldExpand && !open) {
            setOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldExpand]); // Exclude open as it forces expansion constantly
    return [open, setOpen] as const;
};
// Note: Added these for future dynamic sorting capabilities
type ResourceSortFunction = (a: KubernetesLiveStatusResource, b: KubernetesLiveStatusResource) => number;
const resourceSortFunctions: {
    [key: string]: ResourceSortFunction;
} = {
    byName: (a, b) => a.Name.localeCompare(b.Name),
    byHealthStatus: (a, b) => a.HealthStatus.localeCompare(b.HealthStatus),
    // null values go at the end of the alphabet
    bySyncStatus: (a, b) => (a.SyncStatus ?? "z").localeCompare(b.SyncStatus ?? "z"),
    byKind: (a, b) => a.Kind.localeCompare(b.Kind),
    byNameSpace: (a, b) => (a.Namespace ?? emptyNamespaceDisplay).localeCompare(b.Namespace ?? emptyNamespaceDisplay),
};
export function LiveStatusTable({ spaceId, deploymentTargets, argoCDInstances, machineLiveStatusResources, argoCDInstanceLiveStatusResources, resourceSelected }: LiveStatusTableProps) {
    const machineIndexedKubernetesObjects = keyBy(machineLiveStatusResources, (r) => r.MachineId);
    const machineIndexedDeploymentTargets = keyBy(deploymentTargets, (r) => r.Id);
    const argoCDInstanceIndexedKubernetesObjects = keyBy(argoCDInstanceLiveStatusResources, (r) => r.InstanceId);
    const argoCDInstanceLookup = keyBy(argoCDInstances, (r) => r.Id);
    const totalParents = Object.keys(machineIndexedKubernetesObjects).length + Object.keys(argoCDInstanceIndexedKubernetesObjects).length;
    return (<LegacyDataTable title="Live Objects">
            <LegacyDataTableHeader>
                <LegacyDataTableRow>
                    <HeaderCell text="name"/>
                    <HeaderCell text="kind"/>
                    <HeaderCell text="namespace"/>
                    <HeaderCell text="sync status"/>
                    <HeaderCell text="health status"/>
                </LegacyDataTableRow>
            </LegacyDataTableHeader>
            <LegacyDataTableBody>
                {Object.keys(machineIndexedKubernetesObjects).map((machineId) => (<MachineAndChildren key={machineId} spaceId={spaceId} target={machineIndexedDeploymentTargets[machineId] === undefined ? { Name: machineId, Id: machineId } : machineIndexedDeploymentTargets[machineId]} machineResources={machineIndexedKubernetesObjects[machineId]} startExpanded={totalParents === 1} resourceSelected={resourceSelected}/>))}
                {Object.keys(argoCDInstanceIndexedKubernetesObjects).map((instanceId) => (<ArgoCDInstanceAndChildren key={instanceId} spaceId={spaceId} instance={argoCDInstanceLookup[instanceId] === undefined ? { Name: instanceId, Id: instanceId, WebUIUri: null } : argoCDInstanceLookup[instanceId]} instanceResources={argoCDInstanceIndexedKubernetesObjects[instanceId]} startExpanded={totalParents === 1} resourceSelected={resourceSelected}/>))}
            </LegacyDataTableBody>
        </LegacyDataTable>);
}
function HeaderCell(props: {
    text: string;
}) {
    return (<LegacyDataTableRowColumn>
            <h2 className={styles.headerRow}>{props.text}</h2>
        </LegacyDataTableRowColumn>);
}
type MachineAndChildrenProps = {
    spaceId: string;
    target: {
        Name: string;
        Id: string;
    };
    machineResources: KubernetesMachineLiveStatusResource;
    startExpanded: boolean;
    resourceSelected: OnResourceSelected;
};
function MachineAndChildren({ spaceId, target, machineResources, startExpanded, resourceSelected }: MachineAndChildrenProps) {
    // Check if any resources in this machine have failing health status
    const hasFailingResources = React.useMemo(() => {
        return machineResources.Resources.some((resource) => hasFailingResourcesRecursively(resource));
    }, [machineResources.Resources]);
    const shouldStartExpanded = startExpanded || hasFailingResources;
    const [open, setOpen] = useAutoExpansion(shouldStartExpanded);
    const linkToMachine = links.deploymentTargetSettingsPage.generateUrl({ spaceId: spaceId, machineId: target.Id });
    // Hide empty targets
    if (machineResources.Resources.length === 0) {
        return <></>;
    }
    return (<>
            <LegacyDataTableRow displayNoBorder>
                <LegacyDataTableRowColumn colSpan={3} fullWidth={true} fullHeight={false} key={target.Id}>
                    <div className={styles.multiItemCellFlexContainer}>
                        <CollapseIconOrSpacer name={`Toggle ${target.Name}`} showCollapse={machineResources && machineResources.Resources.length > 0} isOpen={open} onClick={() => setOpen(!open)}/>
                        <InternalLink to={linkToMachine} showOpensInNewTabIcon={true} openInSelf={false}>
                            <span className={styles.machineText}>{target.Name}</span>
                        </InternalLink>
                        <span className={styles.machineBadgeWrapper}>
                            {machineResources.Status === "Stale" ? (<Tooltip content={"We haven't received recent live status data from the associated Kubernetes monitor."}>
                                    <Badge size={"small"} variant={"danger"} label={"Kubernetes agent"} icon={<UnavailableIcon size={16}/>}/>
                                </Tooltip>) : (<Badge size={"small"} variant={"neutral"} label={"Kubernetes agent"}/>)}
                        </span>
                    </div>
                </LegacyDataTableRowColumn>
                <LegacyDataTableRowColumn />
                <LegacyDataTableRowColumn className={styles.centredTableContent}>
                    <div className={cx(styles.multiItemCellFlexContainer, styles.fixedWidthStatus)}>
                        <MachineStatusIcon status={machineResources.Status} aria-labelledby={`${machineResources.MachineId}status`}/>
                        <p className={cx(styles.tableCellText, styles.leftPad)} id={`${machineResources.MachineId}status`}>
                            {StatusDisplayLookup[machineResources.Status]}
                        </p>
                    </div>
                </LegacyDataTableRowColumn>
            </LegacyDataTableRow>
            {open &&
            machineResources &&
            machineResources.Resources.sort(resourceSortFunctions.byName).map((resource, index) => (<Row resource={resource} key={`${target.Id}child${index}`} rowKey={`${target.Id}-child-${index}`} indentLevel={1} resourceSelected={resourceSelected}/>))}
        </>);
}
type ArgoCDInstanceAndChildrenProps = {
    spaceId: string;
    instance: Omit<NamedResource, "Links"> & Pick<ArgoCDInstanceSummaryResource, "WebUIUri">;
    instanceResources: KubernetesArgoCDInstanceLiveStatusResource;
    startExpanded: boolean;
    resourceSelected: OnResourceSelected;
};
function ArgoCDInstanceAndChildren({ spaceId, instance, instanceResources, startExpanded, resourceSelected }: ArgoCDInstanceAndChildrenProps) {
    // Check if any resources in this machine have failing health status
    const hasFailingResources = React.useMemo(() => {
        return instanceResources.Resources.some((resource) => hasFailingResourcesRecursively(resource));
    }, [instanceResources.Resources]);
    const shouldStartExpanded = startExpanded || hasFailingResources;
    const [open, setOpen] = useAutoExpansion(shouldStartExpanded);
    const linkToMachine = links.argoCDInstanceApplicationsPage.generateUrl({ spaceId: spaceId, instanceId: instance.Id });
    // Hide empty targets
    if (instanceResources.Resources.length === 0) {
        return <></>;
    }
    return (<>
            <LegacyDataTableRow displayNoBorder>
                <LegacyDataTableRowColumn colSpan={3} fullWidth={true} fullHeight={false} key={instance.Id}>
                    <div className={styles.multiItemCellFlexContainer}>
                        <CollapseIconOrSpacer name={`Toggle ${instance.Name}`} showCollapse={instanceResources && instanceResources.Resources.length > 0} isOpen={open} onClick={() => setOpen(!open)}/>
                        <InternalLink to={linkToMachine} showOpensInNewTabIcon={true} openInSelf={false}>
                            <span className={styles.machineText}>{instance.Name}</span>
                        </InternalLink>
                        <span className={styles.machineBadgeWrapper}>
                            {instanceResources.Status === "Stale" ? (<Tooltip content={"We haven't received recent live status data from this Argo CD instance."}>
                                    <Badge size={"small"} variant={"danger"} label={"Argo CD instance"} icon={<UnavailableIcon size={16}/>}/>
                                </Tooltip>) : (<Badge size={"small"} variant={"neutral"} label={"Argo CD instance"}/>)}
                        </span>
                    </div>
                </LegacyDataTableRowColumn>
                <LegacyDataTableRowColumn />
                <LegacyDataTableRowColumn className={styles.centredTableContent}>
                    <div className={cx(styles.multiItemCellFlexContainer, styles.fixedWidthStatus)}>
                        <MachineStatusIcon status={instanceResources.Status} aria-labelledby={`${instanceResources.InstanceId}status`}/>
                        <p className={cx(styles.tableCellText, styles.leftPad)} id={`${instanceResources.InstanceId}status`}>
                            {StatusDisplayLookup[instanceResources.Status]}
                        </p>
                    </div>
                </LegacyDataTableRowColumn>
            </LegacyDataTableRow>
            {open &&
            instanceResources.Resources.sort(resourceSortFunctions.byName).map((resource, index) => (<Row resource={resource} key={`${instance.Id}child${index}`} rowKey={`${instance.Id}-child-${index}`} indentLevel={1} resourceSelected={resourceSelected}/>))}
        </>);
}
type RowProps = {
    indentLevel: number;
    rowKey: string;
    resource: KubernetesLiveStatusResource;
    resourceSelected?: OnResourceSelected;
};
function Row({ indentLevel, rowKey, resource, resourceSelected }: RowProps) {
    const shouldBeExpanded = isFailingHealthStatus(resource.HealthStatus);
    const [open, setOpen] = useAutoExpansion(shouldBeExpanded);
    return (<>
            <LegacyDataTableRow displayNoBorder>
                <LegacyDataTableRowColumn fullWidth={true} className={styles.centredTableContent}>
                    <NameAndSymbols resource={resource} indentLevel={indentLevel} isOpen={open} onClick={() => setOpen(!open)} resourceSelected={resourceSelected}/>
                </LegacyDataTableRowColumn>
                <TextCell text={resource.Kind}/>
                <TextCell text={resource.Namespace ? resource.Namespace : emptyNamespaceDisplay}/>
                <LegacyDataTableRowColumn className={styles.centredTableContent}>
                    {resource.SyncStatus && (<div className={cx(styles.multiItemCellFlexContainer, styles.fixedWidthStatus)}>
                            <LiveStatusResourceSyncStatusIcon syncStatus={resource.SyncStatus} healthStatus={resource.HealthStatus} aria-labelledby={`${rowKey}status`} lastUpdated={resource.LastUpdated}/>
                            <p className={cx(styles.tableCellText, styles.leftPad)} id={`${rowKey}status`}>
                                {StatusDisplayLookup[resource.SyncStatus]}
                            </p>
                        </div>)}
                </LegacyDataTableRowColumn>
                <LegacyDataTableRowColumn className={styles.centredTableContent}>
                    <div data-chameleon-tour="object-live-status" className={cx(styles.multiItemCellFlexContainer, styles.fixedWidthStatus)}>
                        <LiveStatusResourceHealthStatusIcon status={resource.HealthStatus} aria-labelledby={`${rowKey}status`}/>
                        <p className={cx(styles.tableCellText, styles.leftPad)} id={`${rowKey}status`}>
                            {resource.HealthStatus}
                        </p>
                    </div>
                </LegacyDataTableRowColumn>
            </LegacyDataTableRow>
            {open &&
            resource.Children.sort(resourceSortFunctions.byName).map((resource, index) => (<Row resource={resource} key={`${rowKey}child${index}`} rowKey={`${rowKey}-child-${index}`} indentLevel={indentLevel + 1} resourceSelected={resourceSelected}/>))}
        </>);
}
function TextCell({ text }: {
    text: string;
}) {
    return (<LegacyDataTableRowColumn className={styles.centredTableContent}>
            <p className={styles.tableCellText}>{text}</p>
        </LegacyDataTableRowColumn>);
}
function NameAndSymbols({ indentLevel, isOpen, onClick, resource, resourceSelected }: {
    resource: KubernetesLiveStatusResource;
    indentLevel: number;
    isOpen: boolean;
    onClick: () => void;
    resourceSelected?: OnResourceSelected;
}) {
    const pixelsPerIndent = 40;
    const indent = pixelsPerIndent * indentLevel;
    const hasChildNodes = resource.Children.length > 0;
    const name = useMemo(() => {
        if (resource.HealthStatus === "Missing" || resource.HealthStatus === "Unknown" || !resourceSelected) {
            return <span>{resource.Name}</span>;
        }
        return (<a data-chameleon-tour="object-drawer-link" className={styles.linkStyle} onClick={() => resourceSelected(resource)}>
                {resource.Name}
            </a>);
    }, [resource, resourceSelected]);
    return (<div className={cx(styles.multiItemCellFlexContainer, styles.indented(indent))}>
            <CollapseIconOrSpacer name={`Toggle ${resource.Name}`} showCollapse={hasChildNodes} isOpen={isOpen} onClick={onClick}/>
            <div className={cx(styles.multiItemCellFlexContainer, hasChildNodes ? undefined : styles.leftPad)}>
                <K8sResourceIcon resourceType={resource.Kind}/>
            </div>
            <div className={cx(styles.tableCellText, styles.leftPad)}>{name}</div>
        </div>);
}
function CollapseIconOrSpacer({ name, isOpen, onClick, showCollapse }: {
    name: string;
    showCollapse: boolean;
    isOpen: boolean;
    onClick: () => void;
}) {
    const iconHeight = 24;
    const iconWidth = 48;
    if (showCollapse) {
        return (<div className={styles.iconPadding}>
                <DeprecatedIconButton onClick={onClick} icon={isOpen ? "ChevronDownIcon" : "ChevronRightIcon"} accessibleName={name}/>
            </div>);
    }
    else {
        return <div className={styles.iconSpacer(iconHeight, iconWidth)}/>;
    }
}
const styles = {
    machineText: css({
        font: text.body.bold.medium,
        margin: 0,
    }),
    multiItemCellFlexContainer: css({
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 0,
        height: "100%",
    }),
    tableCellText: css({
        font: text.body.regular.medium,
        display: "inline-block",
        margin: 0,
    }),
    leftPad: css({
        paddingLeft: space[8],
    }),
    centredTableContent: css({
        "&&": { verticalAlign: "middle" },
    }),
    fixedWidthStatus: css({
        width: "105px", //this is literally the width of a "Progressing" word and icon
    }),
    headerRow: css({
        color: themeTokens.color.text.secondary,
        font: text.heading.xSmall,
        margin: 0,
        textTransform: "uppercase",
    }),
    iconPadding: css({
        paddingLeft: space[16],
        paddingRight: space[16],
    }),
    indented(pixels: number) {
        return css({ paddingLeft: pixels });
    },
    iconSpacer(height: number, width: number) {
        return css({
            height: height,
            flexBasis: width,
            flexGrow: 0,
            flexShrink: 0,
        });
    },
    linkStyle: css({
        cursor: "pointer",
    }),
    machineBadgeWrapper: css({
        marginLeft: space[16],
    }),
};
